import { ChevronRight } from 'lucide-react';
import { PropsWithChildren, ReactElement, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from 'src/components/ui/collapsible';
import { Sidebar, SidebarContent, SidebarGroup, SidebarGroupContent, SidebarGroupLabel, SidebarHeader, SidebarMenu } from 'src/components/ui/sidebar';
import { Skeleton } from 'src/components/ui/skeleton';
import useShareSurveyGroup from 'src/hooks/useShareSurveyGroup';
import { defaultQuestion, Question, QuestionStats, SurveyStatus, TotalUsersStats } from 'src/redux/survey/survey.types';
import type { SurveyGroupResultsPayload } from 'src/types/survey-group-api.types';
import { CombinedQuestionStats } from 'src/pages/view-survey/view-survey.types';
import ResultsSidebarHeaderDownload from './components/results-sidebar-header-download';
import ResultsSidebarHeaderShare, { ResultsSidebarHeaderShareProps } from './components/results-sidebar-header-share';
import SurveyResultsSidebarQuestionList from './components/results-sidebar-question-list';
import { ResultsSidebarDetailsRespondents } from './components/results-sidebar-details-section';

const DetailsCollapsible = ({ children }: PropsWithChildren): ReactElement => {
  return (
    <Collapsible
      title="Survey group details"
      className="group/collapsible"
    >
      <SidebarGroup className="px-6 py-3">
        <SidebarGroupLabel
          asChild
          className="group/label h-12 text-base text-sidebar-foreground pt-3 px-0 border-t rounded-none"
        >
          <CollapsibleTrigger>
            Survey group details
            <ChevronRight className="ml-auto transition-transform group-data-[state=open]/collapsible:rotate-90" />
          </CollapsibleTrigger>
        </SidebarGroupLabel>
        <CollapsibleContent>
          <SidebarGroupContent>
            <SidebarMenu>
              <div className="flex flex-col gap-6 py-4">
                {children}
              </div>
            </SidebarMenu>
          </SidebarGroupContent>
        </CollapsibleContent>
      </SidebarGroup>
    </Collapsible>
  );
};

function formatSurveyGroupSurveyCountLabel(count: number): string {
  if (count === 0) {
    return 'No surveys';
  }
  if (count === 1) {
    return '1 survey';
  }
  return `${count} surveys`;
}

const SurveyGroupResultsSidebarHeader = ({
  surveyGroupId,
  surveyGroupTitle,
  surveyCount,
}: {
  surveyGroupId: string;
  surveyGroupTitle: string;
  surveyCount: number;
}): ReactElement => {
  const groupShare = useShareSurveyGroup({
    surveyGroupId,
    surveyStatus: SurveyStatus.Complete,
  });
  const shareProps: ResultsSidebarHeaderShareProps = groupShare.canShare
    ? {
      variant: 'share',
      handleShare: groupShare.handleShare,
      isLoading: groupShare.isLoading,
      shareButtonTitle: 'Copy a publicly shareable link to this survey group’s results',
    }
    : { variant: 'cannotShare' };

  return (
    <SidebarHeader className="p-6 gap-4">
      <div className="flex flex-row gap-2">
        <ResultsSidebarHeaderDownload variant="cannotDownload" />
        <ResultsSidebarHeaderShare {...shareProps} />
      </div>
      <div className="flex flex-col gap-1">
        <span
          className="text-2xl font-medium text-gray-900 truncate"
          title={surveyGroupTitle}
        >
          {surveyGroupTitle}
        </span>
        <span className="text-xs text-gray-500 truncate">
          {formatSurveyGroupSurveyCountLabel(surveyCount)}
        </span>
      </div>
    </SidebarHeader>
  );
};

function buildSidebarQuestions(merged: CombinedQuestionStats[]): { questions: Question[]; questionStats: QuestionStats[] } {
  const questions: Question[] = merged.map((q) => ({
    ...defaultQuestion,
    id: q.id,
    title: q.title,
    type: (q.questionType as string) ?? '',
  }));
  const questionStats: QuestionStats[] = merged.map((q) => q as QuestionStats);
  return { questions, questionStats };
}

type SurveyGroupResultsSidebarProps = {
  surveyGroupId: string;
  /** Display name from group preview (or fallback when preview is not loaded). */
  surveyGroupTitle: string;
  groupResults: SurveyGroupResultsPayload;
  mergedQuestions: CombinedQuestionStats[];
  aggregatedTotalUsers: TotalUsersStats;
  showSummaries: boolean;
};

const Layout = ({ children }: PropsWithChildren): ReactElement => {
  return (
    <Sidebar
      side="right"
      variant="floating"
      size="survey-results"
    >
      {children}
    </Sidebar>
  );
};

export const SurveyGroupResultsSidebar = ({
  surveyGroupId,
  surveyGroupTitle,
  groupResults,
  mergedQuestions,
  aggregatedTotalUsers,
  showSummaries,
}: SurveyGroupResultsSidebarProps): ReactElement => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [isAtBottom, setIsAtBottom] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  const { questions, questionStats } = buildSidebarQuestions(mergedQuestions);

  const onClickQuestion = (index: string): void => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('question', index === 'summary' ? 'summary' : String(Number(index) + 1));
      return next;
    });
  };

  const renderFadeMask = () => {
    if (scrollRef.current) {
      const { scrollHeight, clientHeight, scrollTop } = scrollRef.current;
      const newIsAtBottom = scrollHeight < clientHeight + scrollTop + 1;
      if (newIsAtBottom !== isAtBottom) {
        setIsAtBottom(newIsAtBottom);
      }
    }
  };

  scrollRef.current && renderFadeMask();

  const activeQuestion = searchParams.get('question') ?? (showSummaries ? 'summary' : '0');

  return (
    <Layout>
      <SurveyGroupResultsSidebarHeader
        surveyGroupId={surveyGroupId}
        surveyGroupTitle={surveyGroupTitle}
        surveyCount={groupResults.surveys.length}
      />
      <SidebarContent
        className={`gap-0 ${!isAtBottom ? 'survey-sidebar' : ''}`}
        onScroll={renderFadeMask}
        ref={scrollRef}
      >
        <DetailsCollapsible>
          <ResultsSidebarDetailsRespondents total_users={aggregatedTotalUsers} />
        </DetailsCollapsible>
        {mergedQuestions.length === 0 ? (
          <SidebarGroup className="px-6">
            <Skeleton className="h-8 w-full" />
          </SidebarGroup>
        ) : (
          <SurveyResultsSidebarQuestionList
            variant="data"
            activeQuestion={activeQuestion}
            questions={questions}
            questionStats={questionStats}
            handleClick={onClickQuestion}
            showSummaries={showSummaries}
            surveyStatus={SurveyStatus.Complete}
            hideAnswerLetterPrefix
          />
        )}
      </SidebarContent>
    </Layout>
  );
};
